'use client'

import { Title, Text, Stack, Card, Grid, GridCol, ThemeIcon } from '@mantine/core'
import Section from '@/components/ui/Section'
import { 
  IconSparkles,
  IconVideo,
  IconFileText
} from '@tabler/icons-react'

interface AIFeaturesSectionProps {
  variant?: 'mentor' | 'mentee'
}

export default function AIFeaturesSection({ variant = 'mentee' }: AIFeaturesSectionProps) {
  const isMentorView = variant === 'mentor'
  
  return (
    <Section bg="white" py="xl" contentWidth="lg">
      <Stack gap="xl">
        <div style={{ textAlign: 'center' }}>
          <Title order={2} size="h2" mb="md" c="navy.8">
            {isMentorView ? 'Tools That Save You Time' : 'Everything You Need in One Place'}
          </Title>
          <Text size="lg" c="gray.7" maw={900} mx="auto">
            {isMentorView 
              ? 'Stop taking notes and tracking tasks manually. The platform handles the details so you can focus on the conversation.'
              : 'Get more from your mentorship with tools that track your progress, surface insights, and keep you accountable.'}
          </Text>
        </div>

        <Grid gutter="lg" mt="xl">
          <GridCol span={{ base: 12, md: 4 }}>
            <Card
              padding="lg"
              radius="md"
              h="100%"
              style={{
                backgroundColor: 'white',
                boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)',
                border: '1px solid var(--mantine-color-gray-2)'
              }}
            >
              <Stack gap="md">
                <ThemeIcon size={60} radius="md" variant="light" color="emerald">
                  <IconSparkles size={32} />
                </ThemeIcon>
                <Title order={3} size="h4" c="navy.8">
                  Smart Automation
                </Title>
                <Text size="sm" c="gray.7" style={{ lineHeight: 1.6 }}>
                  {isMentorView
                    ? 'Conversations organized into projects, action items tracked automatically, smart reminders when to follow up, and suggestions on what to discuss next. Focus on mentoring, not admin.'
                    : 'Everything tracked automatically - projects, goals, action items, and reminders. Get conversation suggestions based on your progress. Zero manual work.'}
                </Text>
              </Stack>
            </Card>
          </GridCol>

          <GridCol span={{ base: 12, md: 4 }}>
            <Card
              padding="lg"
              radius="md"
              h="100%"
              style={{
                backgroundColor: 'white',
                boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)',
                border: '1px solid var(--mantine-color-gray-2)'
              }}
            >
              <Stack gap="md">
                <ThemeIcon size={60} radius="md" variant="light" color="violet">
                  <IconVideo size={32} />
                </ThemeIcon>
                <Title order={3} size="h4" c="navy.8">
                  Video Meetings with Transcripts
                </Title>
                <Text size="sm" c="gray.7" style={{ lineHeight: 1.6 }}>
                  {isMentorView
                    ? 'Meet right in the platform with automatic transcriptions. Review what was discussed anytime without taking notes during the call.'
                    : 'Built-in video calls with automatic transcripts. Review past meetings and reference key moments anytime.'}
                </Text>
              </Stack>
            </Card>
          </GridCol>

          <GridCol span={{ base: 12, md: 4 }}>
            <Card
              padding="lg"
              radius="md"
              h="100%"
              style={{
                backgroundColor: 'white',
                boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)',
                border: '1px solid var(--mantine-color-gray-2)'
              }}
            >
              <Stack gap="md">
                <ThemeIcon size={60} radius="md" variant="light" color="blue">
                  <IconFileText size={32} />
                </ThemeIcon>
                <Title order={3} size="h4" c="navy.8">
                  Complete Context
                </Title>
                <Text size="sm" c="gray.7" style={{ lineHeight: 1.6 }}>
                  {isMentorView
                    ? 'All conversations and meetings in one place. Jump back into any discussion with full context - never scramble to remember details.'
                    : 'Your entire mentorship journey organized and searchable. See all discussions, meetings, and progress in one place.'}
                </Text>
              </Stack>
            </Card>
          </GridCol>
        </Grid>
      </Stack>
    </Section>
  )
}
